// @ts-nocheck

import React from 'react';
import { createSelector } from 'reselect';

import { MyMatchAPIGlobalsErrorIds } from '../../constants/mymatch';
import { __ } from '../../i18n/l10n';
import {
  selectBasketCachedMMAPIResponsesInfos,
  selectBasketStatus,
} from '../../selectors/basket/basketPrimary.selector';
import { selectEntitiesBetsEntityBetTitle } from '../../selectors/entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesOutcomes } from '../../selectors/entities/outcomes/entitiesOutcomesPrimary.selector';
import { makeIsAnyOfBasketMatchItemsCashoutingSelector } from '../../selectors/match.selector';
import { type StateType } from '../../states';
import BetBuilder from '../../states/betbuilder';
import { type BetBuilderItem } from '../../states/betbuilder';
import { BasketStatuses } from '../../types/basket';
import { isLabelsTooLong } from '../../utils/basketItems';
import MyMatchMultichoiceLabel from '../MyMatchMultichoiceLabel';

type SelectorProps = {
  matchId: number;
  betId: number;
  outcomeId?: number;
  outcomeIds?: Array<number>;
  prevItem?: BetBuilderItem;
};

const getMatchIdFromProps = (_: StateType, { matchId }: SelectorProps) => matchId;
const getBetIdFromProps = (_: StateType, { betId }: SelectorProps) => betId;
const getOutcomeIdFromProps = (_: StateType, { outcomeId }: SelectorProps) => outcomeId;
const getOutcomeIdsFromProps = (_: StateType, { outcomeIds }: SelectorProps) => outcomeIds;
const getPrevItemFromProps = (_: StateType, { prevItem }: SelectorProps) => prevItem;

const selectIsMultichoice = createSelector(getOutcomeIdsFromProps, (outcomeIds) => outcomeIds?.length > 1);

const selectBetTitle = createSelector(
  [selectEntitiesBetsEntityBetTitle, selectIsMultichoice],
  (betTitle, isMultichoice) => {
    if (!betTitle) {
      return '';
    }
    return isMultichoice ? BetBuilder.addBetTitlePrefix(betTitle) : betTitle;
  },
);

const selectLabels = createSelector(
  [selectIsMultichoice, getOutcomeIdsFromProps, getOutcomeIdFromProps, selectEntitiesOutcomes],
  (isMultichoice, outcomeIds, outcomeId, outcomesState) => {
    if (isMultichoice) {
      return outcomeIds.reduce((acc, curr) => (outcomesState[curr] ? [...acc, outcomesState[curr].label] : acc), []);
    }

    return outcomesState[outcomeId]?.label ? [outcomesState[outcomeId]?.label] : [];
  },
);

const selectShouldBreakLine = createSelector(
  [selectBetTitle, selectLabels],
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  (betTitle, labels) => isLabelsTooLong(`${betTitle} ${labels.join(` ${__('or')} `)}`),
);

const selectLabelComponent = createSelector([selectLabels, selectIsMultichoice], (labels, isMultichoice) =>
  isMultichoice ? <MyMatchMultichoiceLabel labels={labels} /> : <>{labels[0]}</>,
);

const selectHasSpecificError = createSelector(
  [getMatchIdFromProps, getBetIdFromProps, getOutcomeIdFromProps, selectBasketCachedMMAPIResponsesInfos],
  (matchId, betId, outcomeId, cache) =>
    !!~MyMatchAPIGlobalsErrorIds.indexOf(cache[matchId]?.result?.mymatchError?.errorId) ||
    !!cache[matchId]?.result?.mymatchError?.selections?.find((k) => {
      if (k.outcomeId) {
        return k.outcomeId === outcomeId;
      }

      return k.betId === betId;
    }),
);

const selectHasPreviousError = createSelector(
  [getPrevItemFromProps, selectBasketCachedMMAPIResponsesInfos],
  (prevItem, cache) => {
    if (prevItem) {
      const { betId, matchId, outcomeId } = prevItem;

      return (
        !!~MyMatchAPIGlobalsErrorIds.indexOf(cache[matchId]?.result?.mymatchError?.errorId) ||
        !!cache[matchId]?.result?.mymatchError?.selections?.find((k) => {
          if (k.outcomeId) {
            return k.outcomeId === outcomeId;
          }

          return k.betId === betId;
        })
      );
    }

    return false;
  },
);

const selectIsRemovalAllowed = createSelector(
  [selectBasketStatus, makeIsAnyOfBasketMatchItemsCashoutingSelector()],
  (basketStatus, isAnyOfBasketMatchItemsCashouting) =>
    !isAnyOfBasketMatchItemsCashouting &&
    !~[BasketStatuses.PENDING, BasketStatuses.VALIDATED, BasketStatuses.WAITING].indexOf(basketStatus),
);

const selectCardMyMatchContentItemProps = createSelector(
  [
    selectBetTitle,
    selectLabelComponent,
    selectShouldBreakLine,
    selectHasSpecificError,
    selectHasPreviousError,
    selectIsRemovalAllowed,
  ],
  (betTitle, LabelComponent, shouldBreakLine, hasError, hasPreviousError, isRemovalAllowed) => ({
    betTitle,
    LabelComponent,
    shouldBreakLine,
    hasError,
    hasPreviousError,
    isRemovalAllowed,
  }),
);

export default selectCardMyMatchContentItemProps;
